import { Badge } from '@/components/ui/badge';
import type { ConnectionView } from '@/lib/api/generated/model';
import { cn } from '@/lib/utils';

import { useConnectionHealth } from '../hooks/useConnectionHealth';
import { healthState } from './healthState';

/**
 * One connection's health as a chip (spec 229 FR-002): Working, Failing,
 * Not tested or Checking…, from the live test and the recorded health
 * together (`healthState`). The detail — the cause and when it was checked —
 * is the title and part of the accessible name, so a hover and a screen
 * reader hear the same sentence.
 *
 * A Test from the row menu invalidates the health query, which brings the
 * chip back to Checking… without the row changing height.
 */
export function HealthChip({
  workspaceId,
  connection,
  className,
}: {
  workspaceId: string;
  connection: ConnectionView;
  className?: string;
}) {
  const live = useConnectionHealth(workspaceId, connection.id);
  // A re-test after Test or a save keeps the last answer in the cache while it
  // runs; the chip says Checking… for that too, not the stale answer.
  const state = healthState(
    { status: live.isFetching ? 'pending' : live.status, data: live.data },
    connection.health,
  );
  const name = state.detail ? `${state.label}: ${state.detail}` : state.label;

  if (state.tone === 'destructive') {
    return (
      <Badge
        variant="destructive"
        role="status"
        title={state.detail}
        aria-label={name}
        className={cn('shrink-0', className)}
      >
        {state.label}
      </Badge>
    );
  }

  return (
    <span
      role="status"
      title={state.detail}
      aria-label={name}
      className={cn(
        'flex shrink-0 items-center gap-1.5 text-xs whitespace-nowrap',
        state.tone === 'success' && 'text-foreground',
        state.tone === 'warning' && 'text-warning',
        state.tone === 'muted' && 'text-muted-foreground',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'size-2 rounded-full',
          state.tone === 'success' && 'bg-state-success',
          state.tone === 'warning' && 'bg-warning',
          state.tone === 'muted' && 'bg-muted-foreground/40',
          state.kind === 'checking' && 'motion-safe:animate-pulse',
        )}
      />
      {state.label}
    </span>
  );
}
